import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import WarningIcon from "@mui/icons-material/Warning";
import { red } from "@mui/material/colors";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import { CircularProgress, InputAdornment } from "@mui/material";
import { styled } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from '@mui/icons-material/Search';

import MDButton from "components/MDButton";
import Swal from "sweetalert2";
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";
import { ServerBaseUrlForStaticImages } from "constants";
import api from "core/interceptors/api";
import { disableBackNavigation } from "core/utils/storage";

// Styled Input for File Upload
const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

// Columns definition for DataTable, now accepts pagination props for S.No
const columns = (page, rowsPerPage) => [
    {
        Header: "S.No",
        accessor: "s_no",
        align: "center",
        Cell: ({ row }) => (page * rowsPerPage) + row.index + 1, // Calculate serial number
        disableSortBy: true,
    },
    {
        Header: "Description",
        accessor: "description",
        sortable: true,
        align: "left",
    },
    {
        Header: "Image",
        accessor: "image",
        sortable: false,
        align: "center",
        Cell: ({ value }) => (
            <img
                src={`${ServerBaseUrlForStaticImages}/homebanner/${value}`}
                alt="Banner"
                style={{ width: "100px", height: "auto", borderRadius: "5px" }}
                onError={(e) => { e.target.onerror = null; e.target.src = "https://via.placeholder.com/100x70?text=No+Image"; }}
            />
        ),
    },
    {
        Header: "Options",
        accessor: "option",
        align: "center",
    },
];

export const Banner = () => {
    const [allBanners, setAllBanners] = useState([]);
    const [filteredBanners, setFilteredBanners] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [error, setError] = useState(""); // General error for image size etc.
    const [openAddModal, setOpenAddModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);

    const [newBannerInput, setNewBannerInput] = useState({
        description: "",
        status: "Active",
        image: null, // File object for new banner
    });
    const [editInput, setEditInput] = useState({
        id: null,
        description: "",
        status: "Active",
        image: null, // Can be string (existing URL) or File (newly uploaded)
    });
    const [loading, setLoading] = useState(true);
    const [formLoading, setFormLoading] = useState(false);
    const [deleteBannerId, setDeleteBannerId] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    const navigate = useNavigate();

    useEffect(() => {
        disableBackNavigation();
        fetchBanners();
    }, []);

    useEffect(() => {
        const lowerCaseQuery = searchQuery.toLowerCase();
        const newFiltered = allBanners.filter(banner => {
            const descriptionMatch = banner.description?.toLowerCase().includes(lowerCaseQuery);
            return descriptionMatch;
        });
        setFilteredBanners(newFiltered);
        setPage(0);
    }, [searchQuery, allBanners]);

    const fetchBanners = async () => {
        setLoading(true);
        try {
            const res = await api.get("/banners");
            setAllBanners(res.data.banners || []);
        } catch (error) {
            console.error("Error fetching Banners:", error);
            Swal.fire("Error", "Failed to fetch banners. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setOpenAddModal(false);
        setOpenEditModal(false);
        setOpenDeleteModal(false);
        setNewBannerInput({ description: "", status: "Active", image: null });
        setEditInput({ id: null, description: "", status: "Active", image: null });
        setValidationErrors({});
        setError("");
        fetchBanners(); // Ensures data is always refreshed after any modal close
    };

    const handleRowsPerPageChange = (event, newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const paginatedTableData = useMemo(() => {
        return filteredBanners.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [filteredBanners, page, rowsPerPage]);

    const validateField = (name, value, isNew = true) => {
        let err = "";
        switch (name) {
            case "description":
                if (!value || value.trim() === "") {
                    err = "Description is required.";
                } else if (value.trim().length < 5) {
                    err = "Description must be at least 5 characters.";
                }
                break;
            case "image":
                if (isNew && !value) { // Image is required only for new banners
                    err = "Image is required.";
                } else if (value && typeof value !== 'string' && value.size > 2 * 1024 * 1024) {
                    err = "Image must not exceed 2MB.";
                }
                break;
            default:
                break;
        }
        return err;
    };

    const handleAddInputChange = (e) => {
        const { name, value } = e.target;
        setNewBannerInput(prev => ({ ...prev, [name]: value }));
        setValidationErrors(prev => ({ ...prev, [name]: validateField(name, value, true) }));
    };

    const handleAddFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) {
            setNewBannerInput(prev => ({ ...prev, image: null }));
            setValidationErrors(prev => ({ ...prev, image: "Image is required." }));
            return;
        }

        const imageError = validateField("image", file, true);
        if (imageError) {
            setError(imageError);
            setNewBannerInput(prev => ({ ...prev, image: null })); // Clear file if error
            setValidationErrors(prev => ({ ...prev, image: imageError }));
        } else {
            setError("");
            setNewBannerInput(prev => ({ ...prev, image: file }));
            setValidationErrors(prev => ({ ...prev, image: "" }));
        }
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditInput(prev => ({ ...prev, [name]: value }));
        setValidationErrors(prev => ({ ...prev, [name]: validateField(name, value, false) }));
    };

    const handleEditFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) {
            setEditInput(prev => ({ ...prev, image: prev.image && typeof prev.image === 'string' ? prev.image : null }));
            setValidationErrors(prev => ({ ...prev, image: "" }));
            return;
        }

        const imageError = validateField("image", file, false);
        if (imageError) {
            setError(imageError);
            setEditInput(prev => ({ ...prev, image: typeof prev.image === 'string' ? prev.image : null }));
            setValidationErrors(prev => ({ ...prev, image: imageError }));
        } else {
            setError("");
            setEditInput(prev => ({ ...prev, image: file }));
            setValidationErrors(prev => ({ ...prev, image: "" }));
        }
    };

    const validateForm = (data, isNew = true) => {
        let errors = {};
        let isValid = true;

        const descriptionError = validateField("description", data.description, isNew);
        if (descriptionError) { errors.description = descriptionError; isValid = false; }

        if (isNew) { // Only check image requirement for new banner
            const imageError = validateField("image", data.image, isNew);
            if (imageError) { errors.image = imageError; isValid = false; }
        } else { // For edit mode
            // If there's no current image (either existing or new file) AND the field is mandatory on backend
            // This is the tricky part. If your backend *always* expects banner_images and doesn't allow NULL on update
            // then if data.image is null (user removed it), it's a validation error.
            // Otherwise, if backend allows NULL on update, this check shouldn't trigger.
            // For now, assume image is always required or existing is kept.
            if (!data.image && !allBanners.find(b => b.id === data.id)?.banner_images) {
                 errors.image = "Image is required for this banner.";
                 isValid = false;
            } else if (data.image && typeof data.image !== 'string') { // If a new file is provided during edit
                const imageSizeError = validateField("image", data.image, isNew);
                if (imageSizeError) { errors.image = imageSizeError; isValid = false; }
            }
        }

        setValidationErrors(errors);
        return isValid;
    };

    const addBanner = async (event) => {
        event.preventDefault();
        if (!validateForm(newBannerInput, true)) {
            Swal.fire("Validation Error", "Please correct the form errors.", "error");
            return;
        }

        setFormLoading(true);
        const formData = new FormData();
        formData.append("description", newBannerInput.description || "");
        formData.append("status", newBannerInput.status || "Active");
        
        // --- CRITICAL CHECK ---
        // Ensure that newBannerInput.image is a File object before appending.
        // If this check fails, it means client-side logic/validation failed somewhere.
        if (newBannerInput.image instanceof File) { 
            formData.append("banner_images", newBannerInput.image);
        } else {
            console.error("Add Banner: newBannerInput.image is not a File object:", newBannerInput.image);
            Swal.fire("Error", "A valid image file is required for adding a banner.", "error");
            setFormLoading(false);
            return; // STOP EXECUTION as valid file is not present
        }

        try {
            const response = await api.post("/banners", formData);
            if (response.data.success) {
                Swal.fire({ icon: "success", text: "Banner added successfully.", timer: 2000, showConfirmButton: false });
                handleClose();
            } else {
                // Specific backend error message check
                if (response.data.message?.includes("banner_images' cannot be null")) {
                    Swal.fire("Error", "Image is required. Please upload a banner image.", "error");
                } else {
                    Swal.fire({ icon: "error", text: response.data.message || "Server problem. Please try again.", timer: 2000, showConfirmButton: false });
                }
            }
        } catch (err) {
            console.error("Error adding banner:", err);
            // Check for common HTTP status codes for bad requests or specific messages
            if (err.response?.data?.message?.includes("banner_images cannot be null") || err.response?.status === 400) {
                Swal.fire("Error", "Image is required. Please upload a banner image.", "error");
            } else {
                Swal.fire({ icon: "error", text: "An unexpected error occurred. Please try again.", timer: 2000, showConfirmButton: false });
            }
        } finally {
            setFormLoading(false);
        }
    };

    const deleteBanner = async (event) => {
        event.preventDefault();
        setFormLoading(true);
        try {
            const response = await api.put(`/banners/replace/${deleteBannerId}`);
            if (response.data?.success) {
                Swal.fire({ icon: "success", text: response.data.message, timer: 2000, showConfirmButton: false });
                handleClose();
            } else {
                Swal.fire({ icon: "error", text: response.data?.message || "Failed to delete the banner.", timer: 2000, showConfirmButton: false });
            }
        } catch (error) {
            console.error("Error deleting Banner:", error);
            Swal.fire({ icon: "error", text: "An unexpected error occurred. Please try again.", timer: 2000, showConfirmButton: false });
        } finally {
            setFormLoading(false);
        }
    };

    const updateBanner = async (event) => {
        event.preventDefault();
        if (!validateForm(editInput, false)) {
            Swal.fire("Validation Error", "Please correct the form errors.", "error");
            return;
        }

        setFormLoading(true);
        const formData = new FormData();
        formData.append("description", editInput.description || "");
        formData.append("status", editInput.status || "Active");

        if (editInput.image instanceof File) { // Only append if it's a new File object
            formData.append("banner_images", editInput.image);
        } else if (editInput.image === null) {
            // User explicitly cleared the image. Tell backend to remove it.
            // Your backend MUST be designed to handle this `remove_existing_image` flag.
            formData.append("remove_existing_image", "true");
        }
        // If editInput.image is a string (existing URL), we do NOT append 'banner_images'.
        // This relies on your backend's PUT endpoint behavior to preserve existing file if not provided/removed.

        try {
            const response = await api.put(`/banners/${editInput.id}`, formData);
            if (response.data?.success) {
                Swal.fire({ icon: "success", text: response.data.message, timer: 2000, showConfirmButton: false });
                handleClose();
            } else {
                if (response.data.message?.includes("banner_images' cannot be null")) {
                    Swal.fire("Error", "Image is required. Please upload a banner image or revert changes.", "error");
                } else {
                    Swal.fire({ icon: "error", text: response.data?.message || "Failed to update the banner.", timer: 2000, showConfirmButton: false });
                }
            }
        } catch (err) {
            console.error("Error updating banner:", err);
             if (err.response?.data?.message?.includes("banner_images cannot be null") || err.response?.status === 400) {
                 Swal.fire("Error", "Image is required. Please upload a banner image or revert changes.", "error");
            } else {
                Swal.fire({ icon: "error", text: "An unexpected error occurred. Please try again.", timer: 2000, showConfirmButton: false });
            }
        } finally {
            setFormLoading(false);
        }
    };

    const data = useMemo(() => paginatedTableData.map((banner) => ({
        id: banner.id,
        description: banner.description,
        image: banner.banner_images,
        option: (
            <div style={{ display: "flex", justifyContent: "center" }}>
                <IconButton
                    color="success"
                    size="small"
                    onClick={() => {
                        setEditInput({
                            id: banner.id,
                            description: banner.description,
                            status: banner.status,
                            image: banner.banner_images, // Pass existing image URL string
                        });
                        setError("");
                        setValidationErrors({});
                        setOpenEditModal(true);
                    }}
                >
                    <EditIcon />
                </IconButton>
                {!banner.banner_images?.includes("default") && ( // Assuming "default" implies a system-managed image that shouldn't be deletable
                    <IconButton
                        color="error"
                        size="small"
                        style={{ marginLeft: "5px" }}
                        onClick={() => {
                            setDeleteBannerId(banner.id);
                            setOpenDeleteModal(true);
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                )}
            </div>
        ),
    })), [paginatedTableData]);


    return (
        <DashboardLayout>
            <MDBox display="flex" justifyContent="flex-end" alignItems="center">
                <LogoutButton
                    onLogout={() => {
                        navigate("/login");
                    }}
                />
            </MDBox>
            <DashboardNavbar />

            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex" justifyContent="space-between" alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">
                                    Home - Banner Images
                                </MDTypography>
                                
                            </MDBox>
                            <MDBox sx={{ textAlign: "right", mt: 4, mr: 4 }}>
                                <MDButton color="success" onClick={() => setOpenAddModal(true)}>
                                    Add Banner
                                </MDButton>
                            </MDBox>

                            <MDBox pt={3} display="flex" justifyContent="center" alignItems="center" >
                                {loading ? (
                                    <CircularProgress color="primary" />
                                ) : (
                                    <DataTable
                                        table={{ columns: columns(page, rowsPerPage), rows: data }}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }}
                                        showTotalEntries={true}
                                        canSearch={false}
                                        noEndBorder
                                        pagination={{
                                            variant: "contained",
                                            color: "dark",
                                            page: page,
                                            onPageChange: handlePageChange,
                                            rowsPerPage: rowsPerPage,
                                            onRowsPerPageChange: handleRowsPerPageChange,
                                            count: filteredBanners.length,
                                        }}
                                    />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            {/* Add Modal */}
            <Dialog
                open={openAddModal}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>Add Banner</DialogTitle>
                <MDBox component="form" onSubmit={addBanner}>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    type="text"
                                    label="Description"
                                    name="description"
                                    value={newBannerInput.description}
                                    onChange={handleAddInputChange}
                                    helperText={validationErrors.description}
                                    error={!!validationErrors.description}
                                    fullWidth
                                    margin="dense"
                                    required
                                    multiline
                                    rows={3}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <MDBox display="flex" alignItems="center" gap={2}>
                                    <Button
                                        component="label"
                                        variant="contained"
                                        color="info"
                                        startIcon={<PhotoCamera />}
                                    >
                                        Upload Image
                                        <VisuallyHiddenInput type="file" onChange={handleAddFileChange} accept="image/*" />
                                    </Button>
                                    {newBannerInput.image && (
                                        <MDTypography variant="caption">
                                            {newBannerInput.image.name} ({(newBannerInput.image.size / (1024 * 1024)).toFixed(2)} MB)
                                        </MDTypography>
                                    )}
                                </MDBox>
                                {validationErrors.image && (
                                    <MDTypography variant="caption" color="error" display="block">
                                        {validationErrors.image}
                                    </MDTypography>
                                )}
                                {error && ( // General file error (e.g., size limit from handleFileChange)
                                    <MDTypography variant="caption" color="error" display="block">
                                        {error}
                                    </MDTypography>
                                )}
                                {newBannerInput.image && typeof newBannerInput.image !== 'string' && (
                                    <MDBox mt={2}>
                                        <img
                                            src={URL.createObjectURL(newBannerInput.image)}
                                            alt="New Banner Preview"
                                            style={{ width: "200px", height: "auto", borderRadius: "5px", border: "1px solid #ddd" }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setNewBannerInput(prev => ({ ...prev, image: null }));
                                                setValidationErrors(prev => ({ ...prev, image: "Image is required." }));
                                                setError("");
                                            }}
                                            sx={{ ml: 1, color: red[500] }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </MDBox>
                                )}
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={handleClose} color="secondary" disabled={formLoading}>
                            Cancel
                        </MDButton>
                        <MDButton type="submit" color="success" disabled={formLoading}>
                            {formLoading ? <CircularProgress size={24} /> : "Save"}
                        </MDButton>
                    </DialogActions>
                </MDBox>
            </Dialog>

            {/* Edit Modal */}
            <Dialog
                open={openEditModal}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>Edit Banner</DialogTitle>
                <MDBox component="form" onSubmit={updateBanner}>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    type="text"
                                    label="Description"
                                    name="description"
                                    value={editInput.description || ""}
                                    onChange={handleEditInputChange}
                                    helperText={validationErrors.description}
                                    error={!!validationErrors.description}
                                    fullWidth
                                    margin="dense"
                                    required
                                    multiline
                                    rows={3}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <MDBox display="flex" alignItems="center" gap={2}>
                                    <Button
                                        component="label"
                                        variant="contained"
                                        color="info"
                                        startIcon={<PhotoCamera />}
                                    >
                                        Change Image
                                        <VisuallyHiddenInput type="file" onChange={handleEditFileChange} accept="image/*" />
                                    </Button>
                                    {editInput.image && typeof editInput.image === 'object' && (
                                        <MDTypography variant="caption">
                                            {editInput.image.name} ({(editInput.image.size / (1024 * 1024)).toFixed(2)} MB)
                                        </MDTypography>
                                    )}
                                </MDBox>
                                {validationErrors.image && (
                                    <MDTypography variant="caption" color="error" display="block">
                                        {validationErrors.image}
                                    </MDTypography>
                                )}
                                {error && (
                                    <MDTypography variant="caption" color="error" display="block">
                                        {error}
                                    </MDTypography>
                                )}

                                {/* Preview of existing or newly selected image */}
                                {editInput.image && (
                                    <MDBox mt={2} display="flex" alignItems="center" gap={1}>
                                        <img
                                            src={typeof editInput.image === "string"
                                                ? `${ServerBaseUrlForStaticImages}/homebanner/${editInput.image}`
                                                : URL.createObjectURL(editInput.image)
                                            }
                                            alt="Banner Preview"
                                            style={{ width: "200px", height: "auto", borderRadius: "5px", border: "1px solid #ddd" }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setEditInput(prev => ({ ...prev, image: null }));
                                                setValidationErrors(prev => ({ ...prev, image: validateField("image", null, false) }));
                                                setError("");
                                            }}
                                            sx={{ color: red[500] }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </MDBox>
                                )}
                                {!editInput.image && (
                                    <MDTypography variant="caption" color="textSecondary" mt={1}>
                                        No image selected.
                                    </MDTypography>
                                )}
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={handleClose} color="secondary" disabled={formLoading}>
                            Cancel
                        </MDButton>
                        <MDButton type="submit" color="success" disabled={formLoading}>
                            {formLoading ? <CircularProgress size={24} /> : "Save Changes"}
                        </MDButton>
                    </DialogActions>
                </MDBox>
            </Dialog>


            {/* Delete Modal */}
            <Dialog open={openDeleteModal} onClose={handleClose} maxWidth="sm" fullWidth>
                <MDBox component="form" onSubmit={deleteBanner}>
                    <DialogTitle>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item>
                                <WarningIcon
                                    style={{ color: red[700], fontSize: 40 }}
                                />
                            </Grid>
                            <Grid item>
                                <MDTypography variant="h6" component="span">
                                    Confirm Delete
                                </MDTypography>
                            </Grid>
                        </Grid>
                    </DialogTitle>

                    <DialogContent dividers>
                        <MDBox
                            p={2}
                            style={{
                                backgroundColor: red[50],
                                borderLeft: `5px solid ${red[700]}`,
                            }}
                        >
                            <MDTypography variant="body1">
                                Are you sure you want to delete this item? This action cannot be undone.
                            </MDTypography>
                        </MDBox>
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={handleClose} style={{ color: "gray" }} disabled={formLoading}>
                            Cancel
                        </MDButton>
                        <MDButton type="submit" color="primary" disabled={formLoading}>
                            {formLoading ? <CircularProgress size={24} /> : "Delete"}
                        </MDButton>
                    </DialogActions>
                </MDBox>
            </Dialog>

        </DashboardLayout>
    );
};

export default Banner;